import React, { useState, useRef, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import { Tag, TagData, TagColor, TagSize, getColorFromString } from './Tag';

export interface TagInputProps {
  value: TagData[];
  onChange: (tags: TagData[]) => void;
  suggestions?: TagData[];
  onSearch?: (query: string) => void;
  placeholder?: string;
  maxTags?: number;
  allowCreate?: boolean;
  createLabel?: string;
  size?: TagSize;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  className?: string;
}

export function TagInput({
  value,
  onChange,
  suggestions = [],
  onSearch,
  placeholder = 'Add tag...',
  maxTags,
  allowCreate = true,
  createLabel = 'Create',
  size = 'md',
  disabled = false,
  loading = false,
  error,
  className,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input and already selected tags
  const filteredSuggestions = suggestions.filter(
    (s) =>
      s.name.toLowerCase().includes(inputValue.toLowerCase()) &&
      !value.some((v) => v.id === s.id)
  );

  // Check if we can create a new tag with current input
  const canCreate =
    allowCreate &&
    inputValue.trim() &&
    !suggestions.some((s) => s.name.toLowerCase() === inputValue.toLowerCase()) &&
    !value.some((v) => v.name.toLowerCase() === inputValue.toLowerCase());

  const isAtMax = maxTags != null && value.length >= maxTags;

  // Handle adding a tag
  const addTag = useCallback(
    (tag: TagData) => {
      if (isAtMax || disabled) return;
      onChange([...value, tag]);
      setInputValue('');
      setIsOpen(false);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    },
    [value, onChange, isAtMax, disabled]
  );

  // Handle creating a new tag
  const createTag = useCallback(() => {
    if (!canCreate || isAtMax || disabled) return;
    const newTag: TagData = {
      id: `new-${Date.now()}`,
      name: inputValue.trim(),
      color: getColorFromString(inputValue.trim()),
    };
    addTag(newTag);
  }, [canCreate, inputValue, addTag, isAtMax, disabled]);

  // Handle removing a tag
  const removeTag = useCallback(
    (tagId: string) => {
      if (disabled) return;
      onChange(value.filter((t) => t.id !== tagId));
      inputRef.current?.focus();
    },
    [value, onChange, disabled]
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
    onSearch?.(newValue);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const totalItems = filteredSuggestions.length + (canCreate ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
          addTag(filteredSuggestions[highlightedIndex]);
        } else if (highlightedIndex === filteredSuggestions.length && canCreate) {
          createTag();
        } else if (canCreate) {
          createTag();
        }
        break;

      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      case 'Backspace':
        if (!inputValue && value.length > 0) {
          removeTag(value[value.length - 1].id);
        }
        break;

      case ',':
      case 'Tab':
        if (inputValue.trim()) {
          e.preventDefault();
          if (canCreate) {
            createTag();
          } else if (filteredSuggestions.length > 0) {
            addTag(filteredSuggestions[0]);
          }
        }
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const inputSizeClasses = {
    sm: 'min-w-[60px] py-0.5',
    md: 'min-w-[80px] py-1',
    lg: 'min-w-[100px] py-1.5',
  };

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {/* Input container */}
      <div
        className={clsx(
          'flex flex-wrap items-center gap-1.5 px-2 py-1 border rounded-lg bg-white dark:bg-gray-800',
          'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500',
          error ? 'border-red-300' : 'border-gray-300 dark:border-gray-700',
          disabled && 'bg-gray-100 cursor-not-allowed dark:bg-gray-800'
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Selected tags */}
        {value.map((tag) => (
          <Tag
            key={tag.id}
            color={tag.color}
            size={size}
            icon={tag.icon}
            removable
            onRemove={() => removeTag(tag.id)}
            disabled={disabled}
          >
            {tag.name}
          </Tag>
        ))}

        {/* Input field */}
        {!isAtMax && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            placeholder={value.length === 0 ? placeholder : ''}
            disabled={disabled}
            className={clsx(
              'flex-1 border-0 outline-none bg-transparent',
              sizeClasses[size],
              inputSizeClasses[size],
              disabled && 'cursor-not-allowed'
            )}
          />
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 dark:border-gray-700" />
        )}
      </div>

      {/* Error message */}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {/* Max tags message */}
      {isAtMax && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Maximum of {maxTags} tags reached</p>
      )}

      {/* Dropdown */}
      {isOpen && (filteredSuggestions.length > 0 || canCreate) && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto dark:bg-gray-800 dark:border-gray-700">
          {/* Suggestions */}
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => addTag(suggestion)}
              className={clsx(
                'w-full px-3 py-2 text-left flex items-center gap-2 transition-colors',
                sizeClasses[size],
                highlightedIndex === index ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
              )}
            >
              {suggestion.color && (
                <span
                  className={clsx(
                    'h-2 w-2 rounded-full',
                    `bg-${suggestion.color}-500`
                  )}
                  style={{ backgroundColor: getColorValue(suggestion.color) }}
                />
              )}
              <span>{suggestion.name}</span>
              {suggestion.description && (
                <span className="text-gray-400 text-xs ml-auto dark:text-gray-500">{suggestion.description}</span>
              )}
            </button>
          ))}

          {/* Create option */}
          {canCreate && (
            <button
              type="button"
              onClick={createTag}
              className={clsx(
                'w-full px-3 py-2 text-left flex items-center gap-2 transition-colors border-t border-gray-100 dark:border-gray-800',
                sizeClasses[size],
                highlightedIndex === filteredSuggestions.length
                  ? 'bg-blue-50 text-blue-700'
                  : 'hover:bg-gray-50'
              )}
            >
              <span className="text-gray-400 dark:text-gray-500">+</span>
              <span>{createLabel} "{inputValue}"</span>
            </button>
          )}

          {/* No results */}
          {filteredSuggestions.length === 0 && !canCreate && inputValue && (
            <div className={clsx('px-3 py-2 text-gray-500 dark:text-gray-400', sizeClasses[size])}>
              No matching tags
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper to get actual color value
function getColorValue(color: TagColor): string {
  const colors: Record<TagColor, string> = {
    gray: '#6B7280',
    red: '#EF4444',
    orange: '#F97316',
    yellow: '#EAB308',
    green: '#22C55E',
    teal: '#14B8A6',
    blue: '#3B82F6',
    indigo: '#6366F1',
    purple: '#A855F7',
    pink: '#EC4899',
  };
  return colors[color];
}

// Simple variant without dropdown
export interface SimpleTagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  size?: TagSize;
  disabled?: boolean;
  className?: string;
}

export function SimpleTagInput({
  value,
  onChange,
  placeholder = 'Add tag and press Enter',
  maxTags,
  size = 'md',
  disabled = false,
  className,
}: SimpleTagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isAtMax = maxTags != null && value.length >= maxTags;

  const addTag = () => {
    const tag = inputValue.trim();
    if (!tag || isAtMax || disabled || value.includes(tag)) return;
    onChange([...value, tag]);
    setInputValue('');
  };

  const removeTag = (tag: string) => {
    if (disabled) return;
    onChange(value.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-1.5 px-2 py-1 border rounded-lg bg-white dark:bg-gray-800',
        'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500',
        'border-gray-300 dark:border-gray-700',
        disabled && 'bg-gray-100 cursor-not-allowed dark:bg-gray-800',
        className
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((tag) => (
        <Tag
          key={tag}
          color={getColorFromString(tag)}
          size={size}
          removable
          onRemove={() => removeTag(tag)}
          disabled={disabled}
        >
          {tag}
        </Tag>
      ))}

      {!isAtMax && (
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addTag}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          className={clsx(
            'flex-1 min-w-[80px] border-0 outline-none bg-transparent',
            size === 'sm' ? 'text-xs py-0.5' : size === 'lg' ? 'text-base py-1.5' : 'text-sm py-1',
            disabled && 'cursor-not-allowed'
          )}
        />
      )}
    </div>
  );
}
